import AnimatedSection from "./AnimatedSection";
import { useLang } from "@/context/LanguageContext";
import translations from "@/data/translations";
import { ShoppingBag, GraduationCap, Check, MessageCircle } from "lucide-react";

const WHATSAPP_URL = "https://wa.me/34672808030";

const EcommerceSection = () => {
  const { lang } = useLang();
  const t = translations[lang].ecommerce;

  return (
    <section id="ecommerce" data-testid="ecommerce-section" className="bg-navy py-24 md:py-32 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-electric/5 rounded-full -mr-48 -mt-48 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-electric/5 rounded-full -ml-32 -mb-32 blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10">
        <AnimatedSection>
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-semibold text-electric-light mb-4 font-body">
            {t.overline}
          </p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white font-heading mb-4 max-w-2xl">
            {t.headline}
          </h2>
          <p className="text-lg text-slate-300 font-body max-w-2xl mb-14">{t.subheadline}</p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* For Sellers */}
          <AnimatedSection delay={0.1}>
            <div data-testid="ecommerce-sellers" className="bg-white/5 border border-white/10 rounded-2xl p-7 h-full backdrop-blur-sm">
              <div className="w-11 h-11 rounded-xl bg-electric/20 flex items-center justify-center mb-5">
                <ShoppingBag size={22} className="text-electric-light" />
              </div>
              <h3 className="text-xl font-bold text-white mb-5 font-heading">{t.forSellers.title}</h3>
              <div className="space-y-3">
                {t.forSellers.items.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm font-body">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>

          {/* For Academies */}
          <AnimatedSection delay={0.2}>
            <div data-testid="ecommerce-academies" className="bg-white/5 border border-white/10 rounded-2xl p-7 h-full backdrop-blur-sm">
              <div className="w-11 h-11 rounded-xl bg-electric/20 flex items-center justify-center mb-5">
                <GraduationCap size={22} className="text-electric-light" />
              </div>
              <h3 className="text-xl font-bold text-white mb-5 font-heading">{t.forAcademies.title}</h3>
              <div className="space-y-3">
                {t.forAcademies.items.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm font-body">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>

        <AnimatedSection delay={0.3}>
          <div className="mt-10 text-center">
            <a
              data-testid="ecommerce-cta"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-electric text-white font-semibold py-4 px-8 rounded-full hover:bg-electric-hover transition-all duration-300 shadow-lg shadow-electric/30 hover:shadow-electric/50"
            >
              <MessageCircle size={18} />
              {t.cta}
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default EcommerceSection;
